<script lang="ts">
  import Modal from '@shared/ui/Modal.svelte';
  import { competitorServicesApi } from '@shared/api/competitor-services';
  import {
    EXTERNAL_SOLUTION_KIND_OPTIONS,
    SAME_AS_PRODUCT_NAME_MESSAGE,
    normalizeThirdPartySolutionErrorMessage,
    validateThirdPartySolutionName,
  } from '@domain/competitor-service/third-party-solution-edit';
  import type {
    CompetitorService,
    ExternalSolutionKind,
  } from '@shared/types/competitor-service';

  interface Props {
    open: boolean;
    initialValue?: CompetitorService | null;
    blockedNames?: string[];
    blockedNameMessage?: string;
    defaultKind?: ExternalSolutionKind;
    onClose: () => void;
    onSaved: (solution: CompetitorService, submittedKind: ExternalSolutionKind) => void;
  }

  let {
    open,
    initialValue = null,
    blockedNames = [],
    blockedNameMessage = SAME_AS_PRODUCT_NAME_MESSAGE,
    defaultKind = 'other',
    onClose,
    onSaved,
  }: Props = $props();

  let name = $state('');
  let memo = $state('');
  let kind = $state<ExternalSolutionKind>('other');
  let submitting = $state(false);
  let errorMessage = $state<string | null>(null);

  const isEdit = $derived(Boolean(initialValue));

  $effect(() => {
    if (!open) return;
    name = initialValue?.name ?? '';
    memo = initialValue?.memo ?? '';
    kind = initialValue?.kind ?? defaultKind;
    errorMessage = null;
  });

  function close(): void {
    if (submitting) return;
    errorMessage = null;
    onClose();
  }

  async function handleSubmit(ev: SubmitEvent): Promise<void> {
    ev.preventDefault();
    if (submitting) return;
    const trimmedName = name.trim();
    const validationError = validateThirdPartySolutionName(
      name,
      blockedNames,
      blockedNameMessage,
    );
    if (validationError) {
      errorMessage = validationError;
      return;
    }

    submitting = true;
    errorMessage = null;
    try {
      const payload = {
        name: trimmedName,
        memo: memo.trim().length > 0 ? memo.trim() : null,
        kind,
      };
      const saved = initialValue
        ? await competitorServicesApi.patch(initialValue.id, payload)
        : await competitorServicesApi.create(payload);
      onSaved(saved, payload.kind);
    } catch (err) {
      errorMessage =
        err instanceof Error
          ? normalizeThirdPartySolutionErrorMessage(err.message)
          : '保存に失敗しました';
    } finally {
      submitting = false;
    }
  }
</script>

<Modal
  {open}
  title={isEdit ? '外部ソリューションを編集' : '外部ソリューションを追加'}
  onClose={close}
>
  <form id="third-party-solution-form" onsubmit={handleSubmit}>
    <label>
      <span>外部ソリューション名 <em>*</em></span>
      <input
        bind:value={name}
        required
        maxlength="120"
        autocomplete="off"
        placeholder="例: Microsoft 365"
        data-testid="third-party-solution-name-input"
      />
    </label>
    <fieldset>
      <legend>外部ソリューション区分 <em>*</em></legend>
      <div class="radio-group" data-testid="third-party-solution-kind-group">
        {#each EXTERNAL_SOLUTION_KIND_OPTIONS as option}
          <label class="radio-option">
            <input
              type="radio"
              name="third-party-solution-kind"
              value={option.value}
              bind:group={kind}
              data-testid={`third-party-solution-kind-${option.value}`}
            />
            <span>{option.label}</span>
          </label>
        {/each}
      </div>
    </fieldset>
    <label>
      <span>メモ</span>
      <textarea
        bind:value={memo}
        maxlength="2000"
        rows="4"
        placeholder="管理上の補足"
        data-testid="third-party-solution-memo-input"
      ></textarea>
    </label>
    {#if errorMessage}
      <p
        class="server-error"
        role="alert"
        data-testid="third-party-solution-error"
      >
        {errorMessage}
      </p>
    {/if}
  </form>
  {#snippet footer()}
    <button type="button" onclick={close} disabled={submitting}
      >キャンセル</button
    >
    <button
      type="submit"
      form="third-party-solution-form"
      class="primary"
      disabled={submitting || name.trim().length === 0}
      data-testid="third-party-solution-save"
    >
      {submitting ? '保存中…' : '保存'}
    </button>
  {/snippet}
</Modal>

<style>
  form {
    display: grid;
    gap: 12px;
  }
  label {
    display: grid;
    gap: 5px;
    color: #374151;
    font-size: 0.875rem;
    font-weight: 700;
  }
  fieldset {
    border: 0;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 6px;
  }
  legend {
    padding: 0;
    color: #374151;
    font-size: 0.875rem;
    font-weight: 700;
  }
  .radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 14px;
  }
  .radio-option {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-weight: 700;
    color: #374151;
  }
  .radio-option input {
    width: auto;
    margin: 0;
  }
  em {
    color: #dc2626;
    font-style: normal;
  }
  input,
  textarea {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 8px 10px;
    font: inherit;
    color: #1f2937;
  }
  textarea {
    resize: vertical;
    line-height: 1.5;
  }
  .server-error {
    margin: 0;
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 9px 10px;
    font-size: 13px;
  }
</style>
